/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import com.wiz3r.database.Wiz3rDatabase
import models.AdviceCard
import models.AdviceCardCategory
import models.AdviceCardWithStatus
import models.DefaultAdviceCards

/**
 * Web/JS implementation of AdviceCardRepository.
 * Uses awaitAsList() for async WebWorkerDriver compatibility.
 */
class WebAdviceCardRepository(private val database: Wiz3rDatabase) {

    private val queries get() = database.schemaQueries

    /**
     * Seed the database with default advice cards if empty.
     */
    suspend fun seedDefaultCards() {
        // Check if cards already exist
        val existingCards = queries.selectAllActiveAdviceCards().awaitAsList()
        if (existingCards.isNotEmpty()) {
            console.log("📋 Advice cards already exist, skipping seed")
            return
        }

        console.log("🌱 Seeding ${DefaultAdviceCards.cards.size} default advice cards")
        // Insert default cards
        DefaultAdviceCards.cards.forEach { card ->
            queries.insertAdviceCard(
                id = card.id,
                category = card.category.toString(),
                title = card.title,
                content = card.content,
                icon = card.icon,
                priority = card.priority.toLong(),
                is_active = if (card.isActive) 1L else 0L,
                country = card.country
            )
        }
        console.log("✅ Default advice cards seeded")
    }

    /**
     * Get all active advice cards.
     */
    suspend fun getAllActiveCards(): List<AdviceCard> {
        console.log("📖 WebAdviceCardRepository.getAllActiveCards() called")
        val cards = queries.selectAllActiveAdviceCards()
            .awaitAsList()
            .map { it.toAdviceCard() }
        console.log("📖 Got ${cards.size} active cards")
        return cards
    }

    /**
     * Get advice cards by category.
     */
    suspend fun getCardsByCategory(category: AdviceCardCategory): List<AdviceCard> {
        return queries.selectAdviceCardsByCategory(category.toString())
            .awaitAsList()
            .map { it.toAdviceCard() }
    }

    /**
     * Get user's pinned cards.
     */
    suspend fun getPinnedCards(userId: String): List<AdviceCard> {
        return queries.selectPinnedCards(userId)
            .awaitAsList()
            .map { it.toAdviceCard() }
    }

    /**
     * Get cards available for user (not dismissed).
     */
    suspend fun getAvailableCards(userId: String, country: String? = null): List<AdviceCard> {
        console.log("📖 WebAdviceCardRepository.getAvailableCards() called for user: $userId, country: $country")
        val userCountry = country ?: getUserCountry(userId)
        val cards = queries.selectAvailableCards(userId, userCountry ?: "")
            .awaitAsList()
            .map { it.toAdviceCard() }
        console.log("📖 Got ${cards.size} available cards")
        return cards
    }

    suspend fun getUserCountry(userId: String): String? {
        val countryCodes = queries.getUserCountry(userId).awaitAsOneOrNull()
        return countryCodes?.replace("[", "")?.replace("]", "")?.replace("\"", "")?.split(",")?.firstOrNull()?.trim()
    }

    /**
     * Get dismissed cards for a user.
     */
    suspend fun getDismissedCards(userId: String): List<AdviceCard> {
        return queries.selectDismissedCards(userId)
            .awaitAsList()
            .map { it.toAdviceCard() }
    }

    /**
     * Pin a card for a user.
     */
    suspend fun pinCard(userId: String, cardId: String) {
        val id = "${userId}_${cardId}".hashCode().toString()
        queries.pinAdviceCard(
            id = id,
            user_id = userId,
            card_id = cardId
        )
    }

    /**
     * Unpin a card for a user.
     */
    suspend fun unpinCard(userId: String, cardId: String) {
        queries.unpinAdviceCard(userId, cardId)
    }

    /**
     * Dismiss a card for a user.
     */
    suspend fun dismissCard(userId: String, cardId: String) {
        val id = "${userId}_${cardId}".hashCode().toString()
        queries.dismissAdviceCard(
            id = id,
            user_id = userId,
            card_id = cardId
        )
    }

    /**
     * Restore a dismissed card.
     */
    suspend fun restoreCard(userId: String, cardId: String) {
        queries.restoreDismissedCard(userId, cardId)
    }

    /**
     * Mark a card as viewed.
     */
    suspend fun markCardViewed(userId: String, cardId: String) {
        queries.markCardViewed(userId, cardId)
    }

    /**
     * Get cards with their user status.
     */
    suspend fun getCardsWithStatus(userId: String): List<AdviceCardWithStatus> {
        val allCards = getAllActiveCards()
        return allCards.map { card ->
            val status = queries.getUserCardStatus(userId, card.id).awaitAsOneOrNull()
            AdviceCardWithStatus(
                card = card,
                isPinned = status?.is_pinned == 1L,
                isDismissed = status?.is_dismissed == 1L,
                viewedAt = status?.viewed_at
            )
        }
    }

    /**
     * Extension function to convert database row to AdviceCard model.
     */
    private fun com.wiz3r.database.Advice_cards.toAdviceCard(): AdviceCard {
        val card = AdviceCard(
            id = id,
            category = AdviceCardCategory.fromString(category),
            title = title,
            content = content,
            icon = icon,
            priority = priority?.toInt() ?: 0,
            isActive = is_active == 1L,
            country = country
        )
        return card.copy(
            title = card.localizedTitle,
            content = card.localizedContent
        )
    }
}
